import { QueryClient } from '@tanstack/react-query'
import { createRouter as createTanStackRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import type { getAppRouter } from './app-router'
import { RouteErrorComponent } from './components/route-error'
import { hasStoredAuthTokenHint } from './lib/auth-hint'
import { installHiddenFrameFallback } from './lib/hidden-frame-fallback'
import { isMarketingPath } from './lib/marketing-paths'
import { initPostHog } from './lib/posthog'
import { markMarketingBoot } from './lib/router-auth'
import { routeTree } from './routeTree.gen'

/**
 * A client boot on a marketing path gets a Convex-free router: no Convex
 * client, no auth provider, no Sentry — anonymous visitors download none of
 * that. Navigating toward the app is turned into a
 * full-page load by the guard below, and the fresh document boots the full
 * router from `src/app-router.tsx`.
 *
 * The server (SSR and prerender) always builds the full router — its
 * providers emit no DOM, so prerendered marketing HTML hydrates cleanly
 * against the bare client tree.
 */
export async function getRouter() {
  // Before any route mounts: a hidden document fires no animation frames, and
  // overlay-kit opens every overlay inside one. Without this, a page that is
  // hidden at boot — headless, occluded, a background tab — can open no
  // modals at all. No-op on the server and while the page is visible.
  installHiddenFrameFallback()

  if (typeof window !== 'undefined' && isMarketingPath(window.location.pathname)) {
    // Send a signed-in visitor who typed the bare domain to the app.
    //
    // This cannot live in `/`'s `beforeLoad`, which is where it reads like it
    // belongs. `/` is prerendered, so that hook runs at BUILD time — where
    // there is no localStorage and the hint is therefore always false — and
    // the client hydrates the already-resolved match without re-running it.
    // Nothing invalidates it either: `AuthBridge` is the only caller of
    // `router.invalidate()`, and it ships inside the app router, which a
    // marketing boot never loads. The hook still earns its place for
    // in-router navigations back to `/`, which do re-run it; it just never
    // fires for the case it was written for.
    //
    // Only the bare domain redirects. The other marketing pages are ordinary
    // destinations that a signed-in visitor may well have meant to open.
    if (window.location.pathname === '/' && hasStoredAuthTokenHint()) {
      window.location.replace('/home')
      // Freeze the boot while the browser unloads, rather than mounting the
      // marketing page for the frame before it goes away.
      return new Promise<never>(() => {
        // Intentionally never resolves.
      })
    }
    return getMarketingRouter()
  }
  const { getAppRouter: getFullRouter } = await import('./app-router')
  return getFullRouter()
}

function getMarketingRouter() {
  markMarketingBoot()

  // Marketing routes run no queries; the client exists only to satisfy the
  // router context shape shared with the app router.
  const queryClient = new QueryClient()

  const router = createTanStackRouter({
    context: { queryClient },
    defaultErrorComponent: RouteErrorComponent,
    // No preloading: hovering an app link must not run app-route
    // `beforeLoad` gates on a router that has no auth runtime.
    defaultPreload: false,
    routeTree,
    scrollRestoration: true,
  })

  // Navigation guard: any in-router navigation that leaves the marketing
  // surface becomes a full-page load, so the destination boots the full
  // app router (Convex, auth, Sentry). This keeps every `<Link>` on the
  // marketing pages working without a "must use <a>" convention.
  router.subscribe('onBeforeNavigate', ({ toLocation }) => {
    if (!isMarketingPath(toLocation.pathname)) {
      window.location.assign(toLocation.href)
    }
  })

  setupRouterSsrQueryIntegration({ queryClient, router })

  // Surface is resolved from the pathname inside, so this gets the
  // marketing configuration: session replay off, anonymous person profiles.
  initPostHog()

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: Awaited<ReturnType<typeof getAppRouter>>
  }
}

// TanStack Start's HMR can leave the live router holding a stale route tree
// after a route file changes, which silently falls through to the root's
// `notFoundComponent`. Force a full page reload whenever the generated tree
// updates so dev never lands on a phantom 404.
if (import.meta.hot) {
  import.meta.hot.accept(['./routeTree.gen.ts'], () => {
    window.location.reload()
  })
}
